import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Loader2, KeyRound, Mail, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

/**
 * Forgot-password flow — Email OTP (3 steps):
 *   1. Enter email                  → we email a 6-digit code
 *   2. Enter the code               → receive a short-lived reset token
 *   3. Set a new password           → done, sign in
 */
export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [newPwd2, setNewPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const timerRef = useRef(null);

  // Countdown for "Resend code" button on step 2
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    timerRef.current = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [resendIn]);

  const requestOtp = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password/start", { email });
      toast.success("If your account exists, a 6-digit code is on the way.");
      setResendIn(45); // 45-sec cooldown before user can request another code
      setStep(2);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not start recovery");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password/verify", { email, code });
      setResetToken(data.reset_token);
      setStep(3);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not verify code");
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e?.preventDefault();
    if (newPwd !== newPwd2) {
      toast.error("Passwords don't match");
      return;
    }
    if (newPwd.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password/reset", { reset_token: resetToken, new_password: newPwd });
      toast.success("Password updated. Sign in with your new password.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not reset");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-12 lg:py-16">
      <div className="mb-6">
        <Link to="/login" className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252] hover:text-[#0A0A0A]">
          ← Back to sign in
        </Link>
      </div>

      <div className="bg-white border border-[#0A0A0A] p-6">
        <div className="flex items-center gap-3 mb-1">
          <KeyRound className="w-6 h-6 text-[#059669]" strokeWidth={2.5} />
          <h1 className="font-display text-3xl font-black tracking-tighter">Recover access</h1>
        </div>
        <p className="text-xs text-[#525252] mb-6">
          We&rsquo;ll email you a one-time 6-digit code. Expires in 10 minutes.
        </p>

        <Stepper step={step} />

        {step === 1 && (
          <form onSubmit={requestOtp} className="space-y-4 mt-6" data-testid="forgot-step-1">
            <Field label="Account email">
              <input
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                data-testid="forgot-email"
                className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm"
              />
            </Field>
            <Submit loading={loading} testId="forgot-next-1">Email me a code</Submit>
            <p className="text-[10px] text-[#525252]">
              You can request up to 3 codes every 15 minutes.
            </p>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={verifyOtp} className="space-y-4 mt-6" data-testid="forgot-step-2">
            <div className="bg-[#FAFAFA] border-l-2 border-[#059669] px-3 py-2">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">
                <Mail className="w-3.5 h-3.5" /> Check your inbox
              </div>
              <div className="text-xs text-[#0A0A0A]">
                We sent a 6-digit code to <strong className="mono">{email}</strong>.
              </div>
            </div>
            <Field label="6-digit code">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                maxLength={6}
                placeholder="••••••"
                required
                data-testid="forgot-code"
                className="w-full px-3 py-3 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-2xl tracking-[0.5em] text-center mono"
              />
            </Field>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setStep(1)} className="px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-bold border border-[#E5E5E5] text-[#525252] hover:border-[#0A0A0A]">
                Back
              </button>
              <Submit loading={loading} testId="forgot-next-2">Verify code</Submit>
            </div>
            <div className="flex items-center justify-between text-[10px] text-[#525252]">
              <span>3 wrong codes will lock your email for 10 minutes.</span>
              <button
                type="button"
                disabled={resendIn > 0 || loading}
                onClick={requestOtp}
                data-testid="forgot-resend-code"
                className="uppercase tracking-[0.2em] font-bold text-[#0A0A0A] hover:text-[#059669] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
              </button>
            </div>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={resetPassword} className="space-y-4 mt-6" data-testid="forgot-step-3">
            <div className="flex items-center gap-2 bg-[#059669]/10 border-l-2 border-[#059669] px-3 py-2 text-xs text-[#059669] font-bold">
              <CheckCircle2 className="w-4 h-4" /> Identity verified · set a new password
            </div>
            <Field label="New password (min 8 chars)">
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  autoFocus
                  value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)}
                  required
                  minLength={8}
                  data-testid="forgot-new-password"
                  className="w-full px-3 py-2 pr-10 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  data-testid="forgot-password-toggle"
                  aria-label={showPwd ? "Hide passwords" : "Show passwords"}
                  className="absolute inset-y-0 right-0 px-2 flex items-center text-[#525252] hover:text-[#0A0A0A]"
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </Field>
            <Field label="Confirm new password">
              <input
                type={showPwd ? "text" : "password"}
                value={newPwd2}
                onChange={(e) => setNewPwd2(e.target.value)}
                required
                minLength={8}
                data-testid="forgot-new-password-2"
                className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm"
              />
            </Field>
            <Submit loading={loading} testId="forgot-submit">Save new password</Submit>
            <p className="text-[10px] text-[#525252]">This reset session expires in 10 minutes — finish now.</p>
          </form>
        )}
      </div>
    </div>
  );
}

function Stepper({ step }) {
  const items = [
    { n: 1, label: "Email" },
    { n: 2, label: "Code" },
    { n: 3, label: "New password" },
  ];
  return (
    <div className="flex items-center gap-2">
      {items.map((it, i) => (
        <div key={it.n} className="flex items-center gap-2 flex-1 last:flex-initial">
          <div className={`flex items-center gap-2 ${step >= it.n ? "text-[#0A0A0A]" : "text-[#A3A3A3]"}`}>
            <span className={`w-6 h-6 inline-flex items-center justify-center text-[10px] mono font-bold border ${step >= it.n ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5]"}`}>{it.n}</span>
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold">{it.label}</span>
          </div>
          {i < items.length - 1 && (
            <div className={`flex-1 h-px ${step > it.n ? "bg-[#0A0A0A]" : "bg-[#E5E5E5]"}`} />
          )}
        </div>
      ))}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">{label}</label>
      {children}
    </div>
  );
}

function Submit({ loading, children, testId }) {
  return (
    <button
      type="submit"
      disabled={loading}
      data-testid={testId}
      className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#0A0A0A] text-white text-sm uppercase tracking-[0.2em] font-bold hover:bg-[#059669] disabled:opacity-50"
    >
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
}
